import { clsx } from "clsx";
import { type FormEvent, useState } from "react";
import { ZodError } from "zod/v4";
import { Flex } from "@/components/Flex/Flex";
import { Icon } from "@/components/Icon";
import { Text } from "@/components/Text";
import { useFormInstrumentation } from "@/hooks/useFormInstrumentation";
import { type OrderFormData, orderFormSchema } from "@/schemas/order.schema";
import {
  buildConsentRecord,
  buildPaidFormConsents,
  type ConsentRecordClient,
  recordConsents,
} from "@/utils/consents";
import { trackFormFieldError, trackFormSubmit } from "@/utils/tracking";

interface CheckoutFormProps {
  onSubmit: (data: OrderFormData) => Promise<void>;
  isLoading: boolean;
  offerId: string;
  tier: string;
  ofertaUrl: string;
  ofertaProductName: string;
  free?: boolean;
}

function formatPhone(value: string): string {
  const trimmed = value.trim();
  const hasPlus = trimmed.startsWith("+");
  const digits = trimmed.replace(/\D/g, "").slice(0, 15);
  if (!digits) return "";
  return hasPlus ? `+${digits}` : digits;
}

function stripPhone(formatted: string): string {
  const digits = formatted.replace(/\D/g, "").slice(0, 15);
  return digits ? `+${digits}` : "";
}

export function CheckoutForm({
  onSubmit,
  isLoading,
  offerId,
  tier,
  ofertaUrl,
  ofertaProductName,
  free,
}: CheckoutFormProps) {
  const [form, setForm] = useState({
    name: "",
    surname: "",
    email: "",
    phone: "",
    agreedToOferta: false,
    agreedToPrivacy: false,
    agreedToNewsletter: false,
  });
  const [phoneDisplay, setPhoneDisplay] = useState("");
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<string, boolean>>>({});

  // Shares the headless friction core (ADR-0087) so the paid form's
  // form_start/form_field_focus/form_abandon lifecycle can't drift from the free
  // forms'. consents_checked counts the three paid checkboxes (privacy/oferta/
  // newsletter); form_field_error is still fired from the Zod-validation catch.
  const { onFieldFocus, markSubmitted } = useFormInstrumentation(() => ({
    name_filled: Boolean(form.name.trim()),
    email_filled: Boolean(form.email.trim()),
    consents_checked: [form.agreedToPrivacy, form.agreedToOferta, form.agreedToNewsletter].filter(
      Boolean,
    ).length,
    consents_total: 3,
  }));

  const handleChange = (field: string, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handlePhoneChange = (value: string) => {
    const formatted = formatPhone(value);
    setPhoneDisplay(formatted);
    setForm((prev) => ({ ...prev, phone: stripPhone(formatted) }));
    if (errors.phone) setErrors((prev) => ({ ...prev, phone: undefined }));
  };

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched({
      name: true,
      surname: true,
      email: true,
      phone: true,
      agreedToPrivacy: true,
      agreedToOferta: true,
    });

    try {
      const validated = orderFormSchema.parse(form);
      const paid = buildPaidFormConsents({ ofertaUrl, ofertaProductName });
      const now = new Date();
      const records: ConsentRecordClient[] = [
        buildConsentRecord(paid.personalData, now),
        buildConsentRecord(paid.oferta, now),
      ];
      if (validated.agreedToNewsletter) {
        records.push(buildConsentRecord(paid.advMailing, now));
      }
      void recordConsents({
        formId: `checkout:${offerId}`,
        formType: "paid",
        email: validated.email.trim().toLowerCase(),
        name: `${validated.name} ${validated.surname}`.trim(),
        pageUrl: typeof window !== "undefined" ? window.location.href : "",
        consents: records,
        metadata: { offerId, tier, ofertaProductName },
      });
      markSubmitted();
      await onSubmit(validated);
      trackFormSubmit(offerId, tier);
    } catch (err) {
      if (err instanceof ZodError) {
        const newErrors: Record<string, string> = {};
        for (const issue of err.issues) {
          const field = issue.path[0] as string;
          newErrors[field] = issue.message;
          trackFormFieldError(field, "validation");
        }
        setErrors(newErrors);
      }
    }
  };

  const fieldError = (field: string) =>
    touched[field] && errors[field] ? (
      <Text as="p" variant="caption" className="ck-field-error mt-1">
        {errors[field]}
      </Text>
    ) : null;

  return (
    <form data-component="CheckoutForm" onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <input
            type="text"
            placeholder="Имя"
            autoComplete="given-name"
            className={clsx(
              "ck-input h-13 w-full rounded-[var(--corner-md)] text-(length:--type-body-sm)",
              touched.name && errors.name && "ck-input-error",
            )}
            value={form.name}
            onChange={(e) => handleChange("name", e.target.value)}
            onBlur={() => handleBlur("name")}
            onFocus={() => onFieldFocus("name")}
            disabled={isLoading}
          />
          {fieldError("name")}
        </div>
        <div>
          <input
            type="text"
            placeholder="Фамилия"
            autoComplete="family-name"
            className={clsx(
              "ck-input h-13 w-full rounded-[var(--corner-md)] text-(length:--type-body-sm)",
              touched.surname && errors.surname && "ck-input-error",
            )}
            value={form.surname}
            onChange={(e) => handleChange("surname", e.target.value)}
            onBlur={() => handleBlur("surname")}
            onFocus={() => onFieldFocus("surname")}
            disabled={isLoading}
          />
          {fieldError("surname")}
        </div>
      </div>

      <div>
        <input
          type="email"
          placeholder="Email"
          autoComplete="email"
          className={clsx(
            "ck-input h-13 w-full rounded-[var(--corner-md)] text-(length:--type-body-sm)",
            touched.email && errors.email && "ck-input-error",
          )}
          value={form.email}
          onChange={(e) => handleChange("email", e.target.value)}
          onBlur={() => handleBlur("email")}
          onFocus={() => onFieldFocus("email")}
          disabled={isLoading}
        />
        {fieldError("email")}
      </div>

      <div>
        <input
          type="tel"
          placeholder="Телефон с кодом страны"
          autoComplete="tel"
          inputMode="tel"
          className={clsx(
            "ck-input h-13 w-full rounded-[var(--corner-md)] text-(length:--type-body-sm)",
            touched.phone && errors.phone && "ck-input-error",
          )}
          value={phoneDisplay}
          onChange={(e) => handlePhoneChange(e.target.value)}
          onBlur={() => handleBlur("phone")}
          onFocus={() => onFieldFocus("phone")}
          disabled={isLoading}
        />
        {fieldError("phone")}
      </div>

      <div className="space-y-2 pt-2">
        <Flex as="label" align="start" gap={8} className="cursor-pointer">
          <input
            type="checkbox"
            className={clsx(
              "ck-checkbox mt-0.5 flex-shrink-0",
              touched.agreedToPrivacy && errors.agreedToPrivacy && "ck-checkbox-error",
            )}
            checked={form.agreedToPrivacy}
            onChange={(e) => handleChange("agreedToPrivacy", e.target.checked)}
            disabled={isLoading}
          />
          <Text as="span" variant="caption" className="text-(--ck-ink)/70 min-w-0 flex-1">
            Нажимая на кнопку, я даю{" "}
            <a
              href="https://lenacareer.school/personal-data-agreement"
              target="_blank"
              rel="noopener noreferrer"
              className="ck-link"
            >
              согласие
            </a>{" "}
            на обработку моих персональных данных в соответствии с{" "}
            <a
              href="https://lenacareer.school/policy"
              target="_blank"
              rel="noopener noreferrer"
              className="ck-link"
            >
              Политикой
            </a>
            .
          </Text>
        </Flex>
        {fieldError("agreedToPrivacy")}

        <Flex as="label" align="start" gap={8} className="cursor-pointer">
          <input
            type="checkbox"
            className="ck-checkbox mt-0.5 flex-shrink-0"
            checked={form.agreedToNewsletter}
            onChange={(e) => handleChange("agreedToNewsletter", e.target.checked)}
            disabled={isLoading}
          />
          <Text as="span" variant="caption" className="text-(--ck-ink)/70 min-w-0 flex-1">
            Нажимая на кнопку, я даю{" "}
            <a
              href="https://lenacareer.school/adv-agreement"
              target="_blank"
              rel="noopener noreferrer"
              className="ck-link"
            >
              согласие
            </a>{" "}
            на получение информационной и рекламной рассылки.
          </Text>
        </Flex>

        <Flex as="label" align="start" gap={8} className="cursor-pointer">
          <input
            type="checkbox"
            className={clsx(
              "ck-checkbox mt-0.5 flex-shrink-0",
              touched.agreedToOferta && errors.agreedToOferta && "ck-checkbox-error",
            )}
            checked={form.agreedToOferta}
            onChange={(e) => handleChange("agreedToOferta", e.target.checked)}
            disabled={isLoading}
          />
          <Text as="span" variant="caption" className="text-(--ck-ink)/70 min-w-0 flex-1">
            Нажимая на кнопку я принимаю условия{" "}
            <a href={ofertaUrl} target="_blank" rel="noopener noreferrer" className="ck-link">
              Публичной оферты
            </a>{" "}
            на {ofertaProductName}
          </Text>
        </Flex>
        {fieldError("agreedToOferta")}
      </div>

      <button type="submit" className="ck-btn ck-btn-lg w-full rounded-full" disabled={isLoading}>
        {isLoading ? (
          <>
            <span className="ck-spinner" />
            Создаём заказ...
          </>
        ) : (
          <>
            {free ? "Вступить бесплатно" : "Перейти к оплате"}
            <Icon name="arrow-right" size="sm" />
          </>
        )}
      </button>

      <Text as="p" variant="caption" className="text-(--ck-ink)/50 text-center">
        {free
          ? "Нажимая кнопку, вы открываете доступ в клуб."
          : "Вы будете перенаправлены на защищённую страницу оплаты"}
      </Text>
    </form>
  );
}

CheckoutForm.displayName = "CheckoutForm";
